import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Application } from 'pixi.js';
import Stage9x16 from '../components/Stage9x16';
import AudioLoader, { LoadedAudio } from '../components/AudioLoader';
import SpectrogramDisplay, {
  SpectrogramDisplayHandle,
} from '../components/SpectrogramDisplay';
import MessageLog from '../components/MessageLog';
import ExportPanel from '../components/ExportPanel';
import { Button } from '../components/ui/button';
import {
  runWorker,
  STAGE,
  type BeatstageBundleV1,
  type ChartData,
  type BpmSegment,
} from '../worker-messages';
import { TOKENS } from '../tokens';
import { GameClock } from '../timing';
import { mods, type ModsConfig, ALL_MODS, type Mod } from '../mods';
import buildPerfectReplay, { buildHitObjects } from '../utils/perfectReplay';
import { BeatstagePlayer } from '../playBeatstage';
import type { ReplayFrame } from '../lib/utils';
import { Loader2 } from 'lucide-react';

interface TimingPoint {
  time: number;
  bpm: number;
  meter: number;
  offsetMs: number;
}

function timingToSegments(
  timing: TimingPoint[],
  durationMs: number,
  globalOffset: number,
): BpmSegment[] {
  return timing.map((t, i) => ({
    tStartMs: t.time,
    tEndMs: i + 1 < timing.length ? timing[i + 1].time : durationMs,
    bpm: t.bpm,
    offsetMs: t.offsetMs + globalOffset,
    confidence: 1,
  }));
}

const MOD_LABELS: Record<Mod, string> = {
  easy: 'Easy',
  hardRock: 'Hard Rock',
  doubleTime: 'Double Time',
  halfTime: 'Half Time',
  hidden: 'Hidden',
  flashlight: 'Flashlight',
  noFail: 'No Fail',
};

const SEEK_DELTA = 1;

export default function LevelMaker() {
  // Pixi app from Stage9x16
  const [app, setApp] = useState<Application | null>(null);

  // Audio and metadata
  const [audioBlob, setAudioBlob] = useState<File | null>(null);
  const [videoBlob, setVideoBlob] = useState<File | null>(null);
  const [hash, setHash] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [beatGrid, setBeatGrid] = useState<number[]>([]);
  const [subGrid, setSubGrid] = useState<number[]>([]);
  const [segments, setSegments] = useState<TimingPoint[]>([]);
  const [generated, setGenerated] = useState(false);
  const [generating, setGenerating] = useState(false);
  const spectrogramRef = useRef<SpectrogramDisplayHandle | null>(null);
  const inputsHidden = generated || generating;

  const [logMessages, setLogMessages] = useState<string[]>([]);
  const addLog = useCallback((msg: string) => {
    setLogMessages((p) => [...p, msg]);
  }, []);
  const logBoth = useCallback(
    (msg: string) => {
      console.log(msg);
      addLog(msg);
    },
    [addLog],
  );

  // Review state
  const [reviewMode, setReviewMode] = useState(false);
  const [chart, setChart] = useState<ChartData | null>(null);
  const [approved, setApproved] = useState(false);
  const [showPoints, setShowPoints] = useState(true);
  const [showPaths, setShowPaths] = useState(true);
  const [selectedMods, setSelectedMods] = useState<ModsConfig>({});
  const baseBpm = segments[0]?.bpm ?? 120;
  const containerRef = useRef<HTMLDivElement>(null);
  const [playerRef, setPlayerRef] = useState<BeatstagePlayer | null>(null);
  const replayRef = useRef<ReplayFrame[] | null>(null);
  const [savedTime, setSavedTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const prevModsRef = useRef<ModsConfig>({});
  interface ClockState {
    startCtx: number;
    startPerf: number;
  }
  const prevClockRef = useRef<ClockState>({
    startCtx: (GameClock as unknown as ClockState).startCtx ?? 0,
    startPerf: (GameClock as unknown as ClockState).startPerf ?? 0,
  });

  const saveGlobals = useCallback(() => {
    prevModsRef.current = { ...mods };
    const clock = GameClock as unknown as ClockState;
    prevClockRef.current = {
      startCtx: clock.startCtx ?? 0,
      startPerf: clock.startPerf ?? 0,
    };
    for (const key of Object.keys(mods) as (keyof ModsConfig)[]) {
      delete mods[key];
    }
    Object.assign(mods, selectedMods);
  }, [selectedMods]);

  const restoreGlobals = useCallback(() => {
    for (const key of Object.keys(mods) as (keyof ModsConfig)[]) {
      delete mods[key];
    }
    Object.assign(mods, prevModsRef.current);
    const clock = GameClock as unknown as ClockState;
    clock.startCtx = prevClockRef.current.startCtx;
    clock.startPerf = prevClockRef.current.startPerf;
  }, []);

  useEffect(() => {
    if (!reviewMode && playerRef) {
      playerRef.destroy();
      setPlayerRef(null);
      restoreGlobals();
    }
  }, [reviewMode, playerRef, restoreGlobals]);

  useEffect(() => {
    if (playerRef) {
      playerRef.destroy();
      setPlayerRef(null);
      restoreGlobals();
    }
  }, [chart, restoreGlobals]);

  useEffect(() => {
    return () => {
      playerRef?.destroy();
      restoreGlobals();
    };
  }, [playerRef, restoreGlobals]);

  useEffect(() => {
    if (!playerRef || isPaused) return;
    let raf = 0;
    const tick = () => {
      setSavedTime(playerRef.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playerRef, isPaused]);

  const handleAudioLoaded = (info: LoadedAudio) => {
    setAudioBlob(info.file);
    setHash(info.hash);
    setDuration(info.durationMs / 1000);
    setTitle(info.title);
    setArtist(info.artist);
    setBeatGrid([]);
    setSubGrid([]);
    setSegments([]);
    setGenerated(false);
    addLog(`Loaded audio: ${info.file.name}`);
  };

  const handleVideoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoBlob(file);
      addLog(`Loaded video: ${file.name}`);
    }
  }, [addLog]);

  const generate = useCallback(async () => {
    if (!audioBlob) return;
    spectrogramRef.current?.stop();
    setGenerating(true);
    try {
      addLog('Generating chart...');
      const arrayBuffer = await audioBlob.arrayBuffer();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
      await audioCtx.close();

      const channelData = audioBuffer.getChannelData(0);
      const { chart, beats, subbeats } = await runWorker(
        {
          type: 'directedChart',
          sampleRate: audioBuffer.sampleRate,
          channelData,
          width: TOKENS.stage.w,
          height: TOKENS.stage.h,
        },
        addLog,
        [channelData.buffer],
      );
      addLog('Chart generated');
      setChart(chart);
      setBeatGrid(beats);
      setSubGrid(subbeats);
      const segs = beats.slice(0, -1).map((t, i) => ({
        time: t,
        bpm: 60000 / (beats[i + 1] - beats[i] || 500),
        meter: 4,
        offsetMs: 0,
      }));
      setSegments(segs);
      addLog('Chart generation complete');
      setGenerated(true);
    } finally {
      setGenerating(false);
    }
  }, [audioBlob, addLog]);

  const preview = useCallback(async () => {
    if (!audioBlob || !chart || !containerRef.current || !app) return;
    logBoth('Preview started');
    try {
      if (playerRef) {
        logBoth('Destroying existing player');
        playerRef.destroy();
        restoreGlobals();
        setPlayerRef(null);
      }
      saveGlobals();
      logBoth('Building hit objects');
      const hitObjects = buildHitObjects(chart, segments);
      logBoth(`Built ${hitObjects.length} hit objects`);
      const durationMs = duration * 1000;
      const segs = timingToSegments(segments, durationMs, 0);
      logBoth('Constructed timing segments');
      const bundle: BeatstageBundleV1 = {
        version: 1,
        mode: 'circle',
        meta: { title, artist, bpm: baseBpm, offsetMs: 0, stage: STAGE },
        timing: { gridMs: beatGrid, segments: segs },
        supportsMods: {
          easy: true,
          hardRock: true,
          doubleTime: true,
          halfTime: true,
          hidden: true,
          flashlight: true,
          noFail: true,
        },
        hitObjects,
      };
      logBoth('Serializing bundle');
      const { encoded } = await runWorker({ type: 'serialize', bundle });
      logBoth('Bundle serialized');
      const replay = buildPerfectReplay(chart, segments, hitObjects);
      logBoth(`Replay built with ${replay.length} frames`);
      logBoth(
        `Audio blob type: ${audioBlob.type}, size: ${audioBlob.size}`,
      );
      logBoth(
        `Hit objects: ${hitObjects.length}, replay frames: ${replay.length}`,
      );
      logBoth(`Encoded bundle length: ${encoded.length}`);
      logBoth('Creating BeatstagePlayer');
      const newPlayer = await BeatstagePlayer.create(
        encoded,
        audioBlob,
        containerRef.current,
        false,
        app,
        logBoth,
        videoBlob || undefined,
      );
      logBoth('BeatstagePlayer created');
      newPlayer.setSeed(0);
      setPlayerRef(newPlayer);
      replayRef.current = replay;
      setSavedTime(0);
      setIsPaused(false);
      logBoth('Starting playback');
      await newPlayer.playFrom(0, replay);
      logBoth('Playback started');
    } catch (err) {
      console.error('Preview failed', err);
      logBoth(`Preview failed: ${(err as Error).message}`);
    }
  }, [
    audioBlob,
    videoBlob,
    chart,
    containerRef,
    segments,
    title,
    artist,
    baseBpm,
    beatGrid,
    playerRef,
    restoreGlobals,
    saveGlobals,
    duration,
    logBoth,
    app,
  ]);

  const togglePause = useCallback(() => {
    if (!playerRef) return;
    if (isPaused) {
      logBoth(`Resuming from ${savedTime.toFixed(2)}s`);
      void playerRef.playFrom(savedTime, replayRef.current ?? undefined);
      setIsPaused(false);
    } else {
      logBoth('Pausing playback');
      playerRef.stop();
      setSavedTime(playerRef.currentTime);
      setIsPaused(true);
    }
  }, [playerRef, isPaused, savedTime, logBoth]);

  const seek = useCallback(
    (delta: number) => {
      if (!playerRef) return;
      const newTime = Math.max(
        0,
        Math.min(savedTime + delta, playerRef.duration),
      );
      logBoth(`Seeking to ${newTime.toFixed(2)}s`);
      setSavedTime(newTime);
      setIsPaused(false);
      void playerRef.playFrom(newTime, replayRef.current ?? undefined);
    },
    [playerRef, savedTime, logBoth],
  );

  const handleApprove = useCallback(() => {
    if (playerRef) {
      playerRef.destroy();
      setPlayerRef(null);
      restoreGlobals();
    }
    setApproved(true);
  }, [playerRef, restoreGlobals]);


  return (
    <div className="fixed inset-0 bg-ddr-red">
      <div className="absolute top-4 bottom-4 left-4 z-10 mr-4 overflow-y-auto">
        {!reviewMode && (
          <div className="space-y-4">
            {!inputsHidden && (
              <>
                <AudioLoader onLoaded={handleAudioLoaded} onLog={addLog} />
                <div>
                  <label className="text-sm">Background Video (webp)</label>
                  <input
                    type="file"
                    accept="video/webp"
                    onChange={handleVideoChange}
                    className="mt-1 bg-white text-black"
                  />
                </div>
                {audioBlob && (
                  <SpectrogramDisplay
                    ref={spectrogramRef}
                    file={audioBlob}
                    beats={beatGrid}
                    midpoints={subGrid}
                  />
                )}
                {hash && <div className="text-xs">SHA-256: {hash}</div>}
                <div className="flex items-center gap-2">
                  <label htmlFor="title" className="w-20 text-sm">
                    Title
                  </label>
                  <input
                    id="title"
                    type="text"
                    className="w-48 rounded border bg-white px-2 py-1 text-black"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor="artist" className="w-20 text-sm">
                    Artist
                  </label>
                  <input
                    id="artist"
                    type="text"
                    className="w-48 rounded border bg-white px-2 py-1 text-black"
                    value={artist}
                    onChange={(e) => setArtist(e.target.value)}
                  />
                </div>
              </>
            )}
            <div className="flex gap-2">
              {!generated ? (
                <Button onClick={generate} disabled={!audioBlob || generating}>
                  {generating && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Generate
                </Button>
              ) : (
                <Button onClick={() => setReviewMode(true)}>Start Preview</Button>
              )}
            </div>
          </div>
        )}
        {reviewMode && (
          <div className="flex h-full items-start gap-4">
            <div className="flex flex-col gap-2">
              {ALL_MODS.map((m) => (
                <label key={m} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={!!selectedMods[m]}
                    onChange={(e) =>
                      setSelectedMods((p) => ({ ...p, [m]: e.target.checked }))
                    }
                  />
                  {MOD_LABELS[m]}
                </label>
              ))}
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={showPoints}
                  onChange={(e) => setShowPoints(e.target.checked)}
                />
                Points
              </label>
              <label className="flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={showPaths}
                  onChange={(e) => setShowPaths(e.target.checked)}
                />
                Paths
              </label>
              <Button size="sm" onClick={preview}>
                Preview
              </Button>
              {playerRef && (
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={togglePause}>
                    {isPaused ? 'Resume' : 'Pause'}
                  </Button>
                  <Button size="sm" onClick={() => seek(-SEEK_DELTA)}>
                    Back
                  </Button>
                  <Button size="sm" onClick={() => seek(SEEK_DELTA)}>
                    Fwd
                  </Button>
                  <span className="text-xs">
                    {savedTime.toFixed(2)} / {playerRef.duration.toFixed(2)}
                  </span>
                </div>
              )}
              {!approved && (
                <Button size="sm" onClick={handleApprove}>
                  Approve
                </Button>
              )}
              {approved && (
                <ExportPanel
                  meta={{
                    title,
                    artist,
                    bpm: baseBpm,
                    offsetMs: 0,
                    beatDivision: 1,
                    durationMs: duration * 1000,
                  }}
                  chart={chart!}
                  timing={segments}
                  audioBlob={audioBlob!}
                  videoBlob={videoBlob}
                />
              )}
              <Button size="sm" variant="outline" onClick={() => setReviewMode(false)}>
                Restart
              </Button>
            </div>
          </div>
        )}
      </div>
      <div ref={containerRef}>
        <Stage9x16
          onApp={setApp}
          points={reviewMode && chart ? [chart.clickPoints] : undefined}
          paths={reviewMode && chart ? [chart.paths] : undefined}
          showPoints={reviewMode ? showPoints : false}
          showPaths={reviewMode ? showPaths : false}
        />
      </div>
      <MessageLog
        messages={logMessages}
        className="absolute top-0 bottom-0 right-0 m-4 w-[32rem]"
      />
    </div>
  );
}

